import { gsap, ScrollTrigger } from 'gsap/all'
import { useEffect, useRef, MutableRefObject } from 'react'
import { useIsLargeScreen } from 'src/helpers/layout'
import { createShowFromBottomAnimation } from 'src/helpers/animations'

gsap.registerPlugin(ScrollTrigger)

export const useAppPromoSectionAnimations = (
  subtitleRef: MutableRefObject<HTMLDivElement>,
  largeTitleRef: MutableRefObject<HTMLDivElement>,
  sectionRef: MutableRefObject<HTMLDivElement>,
  containerRef: MutableRefObject<HTMLDivElement>,
  imagesRef: MutableRefObject<Array<HTMLDivElement | null>>,
  outerContainerRef: MutableRefObject<HTMLDivElement>,
  imagesCount: number = 0,
  ctaRef?: MutableRefObject<HTMLDivElement | null>
) => {
  const timeline = useRef(gsap.timeline())
  const scrollTimeline = useRef<gsap.core.Timeline | null>(null)
  const isLarge = useIsLargeScreen()

  const animations = () => {
    createShowFromBottomAnimation(timeline.current, subtitleRef.current)
    createShowFromBottomAnimation(timeline.current, largeTitleRef.current)
    if (ctaRef?.current) {
      createShowFromBottomAnimation(timeline.current, ctaRef.current)
    }
    ScrollTrigger.sort()
  }

  useEffect(() => {
    if (isLarge === null) {
      return
    }

    animations()

    // Scroll-bound animation for images stacking (desktop and mobile)
    if (typeof window !== 'undefined' && outerContainerRef.current && sectionRef.current && imagesRef.current.length > 1) {
      // Kill previous scroll timeline if it exists
      if (scrollTimeline.current) {
        scrollTimeline.current.scrollTrigger?.kill(true)
        scrollTimeline.current.kill()
      }
      ScrollTrigger.getAll().forEach(t => {
        if (t.trigger === outerContainerRef.current || t.trigger === sectionRef.current) t.kill(true)
      })

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: '+=150%', // Scroll distance for 3 images
          pin: outerContainerRef.current,
          pinSpacing: true,
          scrub: 1,
          anticipatePin: 1,
          fastScrollEnd: true,
          refreshPriority: 1,
        }
      })

      scrollTimeline.current = tl

      ScrollTrigger.sort()
      ScrollTrigger.refresh()

      // Animation sequence for stacking
      const images = imagesRef.current.filter(img => img !== null) as HTMLDivElement[]
      
      images.forEach((img, index) => {
        if (index === 0) return // First image is already visible

        const currentImg = img.querySelector('img')
        const prevImg = images[index - 1].querySelector('img')

        // The previous image scales down and sharpens corners
        tl.to(images[index - 1], {
          scale: 0.8,
          borderRadius: '16px',
          duration: 1,
          ease: 'none'
        }, index - 1)

        // Internal parallax: previous image scales up slightly inside while shrinking
        if (prevImg) {
          tl.to(prevImg, {
            scale: 1.1,
            duration: 1,
            ease: 'none'
          }, index - 1)
        }

        // The current image comes from the bottom
        gsap.set(img, { yPercent: 100 })
        if (currentImg) {
          gsap.set(currentImg, { scale: 1.1 })
        }

        tl.to(img, {
          yPercent: 0,
          duration: 1,
          ease: 'none'
        }, index - 1)

        if (currentImg) {
          tl.to(currentImg, {
            scale: 1,
            duration: 1,
            ease: 'none'
          }, index - 1)
        }
      })

      return () => {
        if (scrollTimeline.current) {
          scrollTimeline.current.scrollTrigger?.kill(true)
          scrollTimeline.current.kill()
        }
        ScrollTrigger.getAll().forEach(t => {
          if (t.trigger === outerContainerRef.current || t.trigger === sectionRef.current) t.kill(true)
        })
      }
    }
  }, [isLarge, imagesCount])
}

export default {}
